import { SessionWithOrdersAndType } from '@/types/session-interfaces';
import { Folder, FolderWithSessions } from '@/types/folder-interfaces';
import { Pagination } from '@/types/pagination-interfaces';
import { Order } from '@/types/order-interfaces';
import { getSessionListPagination, getSessionList, updateSession, archiveSession, attachOrdersToSessions, unarchiveSession } from '@/services/sessions';
import { getFolder, getFolderListWithSessionsPagination, getFolderListWithSessions, createFolder, updateFolder, archiveFolder, unarchiveFolder } from '@/services/folders';
import { getOrders, getNoteStatusFromOrderStatus } from '@/services/orders';
import { Upload } from '@/types/upload-interfaces';


interface ItemState {
    allItems: (SessionWithOrdersAndType | FolderWithSessions)[];
    allFolders: FolderWithSessions[];
    allSessions: SessionWithOrdersAndType[];
    archivedItems: (SessionWithOrdersAndType | FolderWithSessions)[];
    selectedItems: (SessionWithOrdersAndType | FolderWithSessions)[];
    allOrders: Order[];
    // Simple optimistic update tracking
    pendingOperations: Map<string, PendingOperation>;
    foldersListLoading: boolean;
    allItemsListLoading: boolean;
    archivedListLoading: boolean;
    sessionPagination: Pagination|undefined;
    folderPagination: Pagination|undefined;
    error: string | null;
}

interface PendingOperation {
    id: string;
    type: 'create' | 'update' | 'delete' | 'archive' | 'unarchive' | 'move';
    itemId: string;
    timestamp: number;
    rollbackAction: () => void; // Function to revert the optimistic change
  }
  

export default {
    namespaced: true,
    state: (): ItemState => ({
        allItems: [],
        allFolders: [],
        allSessions: [],
        archivedItems: [],
        selectedItems: [],
        allOrders: [],
        pendingOperations: new Map(),
        foldersListLoading: false,
        allItemsListLoading: false,
        archivedListLoading: false,
        sessionPagination: undefined,
        folderPagination: undefined,
        error: null,
      }),
    mutations: {
        setAllItems(
            state: { allItems: (SessionWithOrdersAndType | FolderWithSessions)[] },
            items: (SessionWithOrdersAndType | FolderWithSessions)[]
        ) {
            state.allItems = items;
        },

        setAllFolders(
            state: { allFolders: FolderWithSessions[] },
            folders: FolderWithSessions[]
        ) {
            state.allFolders = folders;
        },

        setAllSessions(
            state: { allSessions: SessionWithOrdersAndType[] },
            sessions: SessionWithOrdersAndType[]
        ) {
            state.allSessions = sessions;
        },

        setArchivedItems(
            state: { archivedItems: (SessionWithOrdersAndType | FolderWithSessions)[] },
            items: (SessionWithOrdersAndType | FolderWithSessions)[]
        ) {
            state.archivedItems = items;
        },


        updateItem(state: ItemState, { id, updates }: { id: string, updates: Partial<SessionWithOrdersAndType | FolderWithSessions> }) {
            // Update in allItems
            const allItemsIndex = state.allItems.findIndex(item => item.id === id);
            if (allItemsIndex !== -1) {
                state.allItems[allItemsIndex] = { ...state.allItems[allItemsIndex], ...updates };
            }
            
            // Update in archivedItems if present
            const archivedIndex = state.archivedItems.findIndex(item => item.id === id);
            if (archivedIndex !== -1) {
                state.archivedItems[archivedIndex] = { ...state.archivedItems[archivedIndex], ...updates };
            }

            // Update in allFolders if present
            const allFolderIndex = state.allFolders.findIndex(item => item.id === id);
            if (allFolderIndex !== -1) {
                state.allFolders[allFolderIndex] = { ...state.allFolders[allFolderIndex], ...updates };
            }

            // Update in allSessions if present
            const allSessionIndex = state.allSessions.findIndex(item => item.id === id);
            if (allSessionIndex !== -1) {
                state.allSessions[allSessionIndex] = { ...state.allSessions[allSessionIndex], ...updates };
            }
            
            // Update in selectedItems if present
            const selectedIndex = state.selectedItems.findIndex(item => item.id === id);
            if (selectedIndex !== -1) {
                state.selectedItems[selectedIndex] = { ...state.selectedItems[selectedIndex], ...updates };
            }
        },

        setSelection(
            state: { selectedItems: (SessionWithOrdersAndType | FolderWithSessions)[] },
            items: ( SessionWithOrdersAndType | FolderWithSessions )[]
        ) {
                state.selectedItems = items;
        },

        setOrders(
            state: { allOrders: Order[] },
            orders: Order[],
        ) {
            state.allOrders = orders;
        },

        setSessionPagination(
            state: { sessionPagination: Pagination },
            sessionPagination: Pagination,
        ) {
            state.sessionPagination = sessionPagination;
        },

        setFolderPagination(
            state: { folderPagination: Pagination },
            folderPagination: Pagination,
        ) {
            state.folderPagination = folderPagination;
        },

        clearSelection(state: { selectedItems: (SessionWithOrdersAndType | Folder | FolderWithSessions)[] }
        ) {
            state.selectedItems = [];
        },

        restoreItemToPosition(state: ItemState, { item, array, index }: { item: SessionWithOrdersAndType | FolderWithSessions, array: 'allItems' | 'archivedItems' | 'allFolders' | 'allSessions', index: number }) {
            if (array === 'allFolders') {
                if ('sessions' in item) {
                    state.allFolders.splice(index, 0, item as FolderWithSessions);
                }
            } else if (array === 'allSessions') {
                state.allSessions.splice(index, 0, item as SessionWithOrdersAndType);
            }
        },

        removeItem(state: ItemState, itemId: string) {
            state.allItems = state.allItems.filter(item => item.id !== itemId);
            state.allFolders = state.allFolders.filter(item => item.id !== itemId);
            state.allSessions = state.allSessions.filter(item => item.id !== itemId);
            state.archivedItems = state.archivedItems.filter(item => item.id !== itemId);
            state.selectedItems = state.selectedItems.filter(item => item.id !== itemId);
        },

        // Add session to an archived folder's sessions array (used to rollback failed unarchive)
        addSessionToArchivedFolder(state: ItemState, { folderId, session, index }: { folderId: string, session: SessionWithOrdersAndType, index?: number }) {
            const folder = state.archivedItems.find(item => 
                item.type === 'folder' && item.id === folderId
            );
            if (folder && 'sessions' in folder) {
                if (typeof index === 'number' && index >= 0) {
                    folder.sessions.splice(index, 0, session);
                } else {
                    folder.sessions.push(session);
                }
            }
        },

        // Enhanced addItemToArray to support index positioning (optional)
        addItemToArray(state: ItemState, { arrayName, item, index }: { arrayName: 'allItems' | 'allFolders' | 'allSessions' | 'archivedItems', item: SessionWithOrdersAndType | FolderWithSessions, index?: number }) {
            const array = state[arrayName];
            if (typeof index === 'number' && index >= 0) {
                array.splice(index, 0, item);
            } else {
                (array as (SessionWithOrdersAndType | FolderWithSessions)[]).unshift(item);
            }
        },

        removeItemFromArray(state: ItemState, { arrayName, id }: { arrayName: 'allItems' | 'allFolders' | 'allSessions' | 'archivedItems', id: string }) {
            const array = state[arrayName];
            const index = array.findIndex(item => item.id === id);
            if (index !== -1) {
                array.splice(index, 1);
            }
        },

        // Remove session from an archived folder's sessions array
        removeSessionFromArchivedFolder(state: ItemState, { folderId, sessionId }: { folderId: string, sessionId: string }) {
            const folder = state.archivedItems.find(item => 
                item.type === 'folder' && item.id === folderId
            );
            if (folder && 'sessions' in folder) {
                const sessionIndex = folder.sessions.findIndex(session => session.id === sessionId);
                if (sessionIndex !== -1) {
                    folder.sessions.splice(sessionIndex, 1);
                }
            }
        },

        // Add session to a folder's sessions array
        addSessionToFolder(state: ItemState, { folderId, session, index }: { folderId: string, session: SessionWithOrdersAndType, index?: number }) {
            const folder = state.allFolders.find(folder => folder.id === folderId);
            if (folder && 'sessions' in folder && Array.isArray(folder.sessions)) {
                if (typeof index === 'number' && index >= 0) {
                    folder.sessions.splice(index, 0, session);
                } else {
                    folder.sessions.push(session);
                }
            }
        },

        // Remove session from a folder's sessions array
        removeSessionFromFolder(state: ItemState, { folderId, sessionId }: { folderId: string, sessionId: string }) {
            const folder = state.allFolders.find(folder => folder.id === folderId);
            if (folder && folder.sessions) {
                const sessionIndex = folder.sessions.findIndex(session => session.id === sessionId);
                if (sessionIndex !== -1) {
                    folder.sessions.splice(sessionIndex, 1);
                }
            }
        },

        processFolderSessions(state: ItemState, { folder, array }: { folder: FolderWithSessions, array: string }) {
            const targetArray = state[array as keyof ItemState] as FolderWithSessions[];
            const folderIndex = targetArray.findIndex(f => f.id === folder.id);
            
            if (folderIndex !== -1 && targetArray[folderIndex].sessions) {
                targetArray[folderIndex].sessions = folder.sessions;
                const processedSessions = targetArray[folderIndex].sessions.map((session) => {
                    const { noteStatus, orderStatus } = getNoteStatusFromOrderStatus(
                        'orders' in session ? session.orders : []
                    );
        
                    return {
                        ...session,
                        type: "session",
                        noteStatus,
                        orderStatus,
                    };
                });
                
                targetArray[folderIndex].sessions = processedSessions;
            }
        },

        // Update folderId in selectedItems
        updateSelectedItemFolderId(state: ItemState, { itemId, folderId }: { itemId: string, folderId: string | null }) {
            const selectedItem = state.selectedItems.find(i => i.id === itemId);
            if (selectedItem && 'folderId' in selectedItem) {
                (selectedItem as any).folderId = folderId;
            }
        },

        // Update session within a folder (both active and archived)
        updateSessionInFolder(state: ItemState, { folderId, sessionId, updates, isArchived }: { folderId: string, sessionId: string, updates: Partial<SessionWithOrdersAndType>, isArchived: boolean }) {
            const searchArray = isArchived ? state.archivedItems : state.allFolders;
            const folder = searchArray.find(item => 
                item.type === 'folder' && item.id === folderId
            );
            
            if (folder && 'sessions' in folder && folder.sessions) {
                const session = folder.sessions.find(session => session.id === sessionId);
                if (session) {
                    Object.assign(session, updates);
                }
            }
        },

        // Update item in a specific array
        updateItemInArray(state: ItemState, { arrayName, id, updates }: { arrayName: 'allItems' | 'allFolders' | 'allSessions' | 'archivedItems', id: string, updates: Partial<SessionWithOrdersAndType | FolderWithSessions> }) {
            const array = state[arrayName];
            const item = array.find(item => item.id === id);
            if (item) {
                Object.assign(item, updates);
            }
        },

        // Add upload to a session (allSessions array)
        addUploadToSession(state: ItemState, { sessionId, upload }: { sessionId: string, upload: Upload }) {
            const session = state.allSessions.find(s => s.id === sessionId);
            if (session) {
              // Initialize uploads array if it doesn't exist
              if (!session.uploads) {
                session.uploads = [];
              }
              session.uploads.push(upload);
            }
        },

        // Add upload to a session (allFolders array)
        addUploadToFolderSession(state: ItemState, { sessionId, upload }: { sessionId: string, upload: Upload }) {
            // Find the folder that contains this session
            for (const folder of state.allFolders) {
                if (folder.sessions) {
                    const session = folder.sessions.find(s => s.id === sessionId);
                    if (session) {
                        // Initialize uploads array if it doesn't exist
                        if (!session.uploads) {
                            session.uploads = [];
                        }
                        session.uploads.push(upload);
                        return; // Exit once we find and update the session
                    }
                }
            }
        },

        // Pending operations management
        addPendingOperation(state: ItemState, operation: PendingOperation) {
            state.pendingOperations.set(operation.id, operation);
        },
    
        removePendingOperation(state: ItemState, operationId: string) {
            state.pendingOperations.delete(operationId);
        },

        setError(state: ItemState, error: string | null) {
            state.error = error;
        },

        clearAllData(state: ItemState) {
            state.allItems = [];
            state.allFolders = [];
            state.allSessions = [];
            state.archivedItems = [];
            state.selectedItems = [];
            state.allOrders = [];
            state.pendingOperations.clear();
            state.sessionPagination = undefined;
            state.folderPagination = undefined;
            state.error = null;
        }
    },
    actions: {
        // Data fetching actions - these should be called from components
        async fetchAllData({ dispatch }: { dispatch: Function }, params: {sortField: string, sortOrder: string}) {
            // Fetch all items and folders, then update the state
            // Supporting pagination
            const { sortField, sortOrder } = params

            try {
                const [
                    { sessions: rawSessions, pagination: sessionPagination },
                    { folders: rawFolders, pagination: folderPagination },
                    foldersWithSessionAndType,
                    orders,
                    archivedSessions,
                    archivedFolders,
                ] = await Promise.all([
                    getSessionListPagination({perPage: 10, currentPage: 1, orderBy: sortField ?? 'createdAt', order: sortOrder ?? 'desc'}),
                    getFolderListWithSessionsPagination({perPage: 10, currentPage: 1, orderBy: "createdAt"}),
                    getFolderListWithSessions(),
                    getOrders(),
                    getSessionList("includeArchived=only"),
                    getFolderListWithSessions("includeArchived=only"),
                ]);
                const sessions = attachOrdersToSessions(rawSessions, orders);
                const archivedSessionsWithOrders = attachOrdersToSessions(archivedSessions, orders);
                const folders = rawFolders.map((folderObj: Folder) => ({
                    ...folderObj,
                    type: "folder",
                }));
                const archivedFoldersWithType = archivedFolders.map((folderObj: Folder) => ({
                    ...folderObj,
                    type: "folder",
                })) as FolderWithSessions[];

                const allActiveSessions = sessions.filter(item => item.archivedAt === null).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                const tempAllItems = [...sessions, ...folders];

                const allItems = tempAllItems.filter(item => item.archivedAt === null).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                const archivedItems: (FolderWithSessions | SessionWithOrdersAndType)[] = [...archivedFoldersWithType]

                archivedSessionsWithOrders.forEach((session) => {
                    if (!session.folderId) {
                        archivedItems.push(session);
                    }
                });
                
                const allFolders = foldersWithSessionAndType
                    .filter((folder: Folder) => folder.archivedAt === null)
                    .map((folder: Folder) => ({
                        ...folder,
                        type: 'folder',
                    })) as FolderWithSessions[];

                dispatch('updateAllItems', allItems);
                dispatch('updateAllFolders', allFolders);
                dispatch('updateAllSessions', allActiveSessions);
                dispatch('updateArchivedItems', archivedItems);
                dispatch('updateOrders', orders);
                dispatch('updateSessionPagination', sessionPagination);
                dispatch('updateFolderPagination', folderPagination);  
            } catch (error: any) {
                console.error('Failed to fetch data:', error);
                throw error;
            }
        },

        //Refreshes the list of sessions - normally triggered by changing the sort order or sort field
        async reloadSessions({ commit, state }: { commit: Function; state: ItemState }, params: {sortField: string, sortOrder: string}) {
            const { sortField, sortOrder } = params
            try {
                const { sessions: rawSessions, pagination: sessionPagination } = await getSessionListPagination({perPage: 10, currentPage: 1, orderBy: sortField ?? 'createdAt', order: sortOrder ?? 'desc'})
                const sessions = attachOrdersToSessions(rawSessions, state.allOrders)

                commit('setSessionPagination', sessionPagination)
                commit('setAllSessions', sessions);
            } catch (error: any) {
                console.error('Failed to fetch data:', error);
                throw error;
            }
        },

        async fetchAllDataMore({ commit, state }: { commit: Function; state: ItemState }, params: {sortField: string, sortOrder: string}) {
            const { sortField, sortOrder } = params
            try {
                let recentData = [ ...state.allSessions ];
                if (state.sessionPagination && state.sessionPagination.nextPage != null) {
                    const currentPage = state.sessionPagination === null ? 1 : state.sessionPagination.currentPage + 1;
                    const { sessions: rawSessions, pagination: sessionPagination } = await getSessionListPagination({perPage: 10, currentPage, orderBy: sortField ?? 'createdAt', order: sortOrder ?? 'desc'});
                    const sessions = attachOrdersToSessions(rawSessions, state.allOrders);

                    if (sessions) {
                        recentData = [ ...recentData, ...sessions ];
                    }
                    commit('setSessionPagination', sessionPagination);
                }
            
                recentData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                commit('setAllSessions', recentData);
            } catch (error: any) {
                console.error('Failed to fetch data:', error);
                throw error;
            }
        },

        // Only used for Folders for now (could be used for Sessions in the future, migrate to this when re-writing Instant Session functionality)
        async createItem({ commit, state }: { commit: Function; state: ItemState }, item: (Partial<FolderWithSessions | SessionWithOrdersAndType>)) {
    
            try {
                let result;
                if (!item.title) {
                    throw new Error('Item title is required');
                }
                if (item.type === 'folder'){
                    result = await createFolder(item.title as string);
                }
                // Add the real item with server-generated ID
                if (result) {
                    // Add to allItems
                    const allItems = [...state.allItems];
                    allItems.unshift(result);
                    commit('setAllItems', allItems);

                    if (item.type === 'folder') {
                        // Add to allFolders
                        result.type = 'folder';
                        result.sessions = [];
                        const allFolders = [...state.allFolders];
                        allFolders.unshift(result);
                        commit('setAllFolders', allFolders);
                    }
                }
                
                return result;
            } catch (error: any) {
                commit('setError', `Failed to create item: ${error.message}`);
                throw error;
            }
        },

        updateAllItems(
            { commit }: { commit: Function },
            items: (SessionWithOrdersAndType | FolderWithSessions | Folder)[]
        ) {
            commit('setAllItems', items);
        },

        updateAllFolders(
            { commit }: { commit: Function },
            folders: FolderWithSessions[]
        ) {
            commit('setAllFolders', folders);
        },

        updateAllSessions(
            { commit }: { commit: Function },
            sessions: SessionWithOrdersAndType[]
        ) {
            commit('setAllSessions', sessions);
        },

        updateArchivedItems(
            { commit }: { commit: Function },
            items: (SessionWithOrdersAndType | FolderWithSessions)[]
        ) {
            commit('setArchivedItems', items);
        },

        updateSelection({ commit }: { commit: Function }, items: (SessionWithOrdersAndType | FolderWithSessions)[]) {
            commit('setSelection', items);
        },

        updateOrders(
            { commit }: { commit: Function },
            orders: Order[],
        ) {
            commit('setOrders', orders);
        },

        updateSessionPagination(
            { commit }: { commit: Function },
            sessionPagination: Pagination,
        ) {
            commit('setSessionPagination', sessionPagination);
        },

        updateFolderPagination(
            { commit }: { commit: Function },
            folderPagination: Pagination,
        ) {
            commit('setFolderPagination', folderPagination);
        },

        clearSelection({ commit }: { commit: Function }) {
            commit('clearSelection');
        },

        async updateItem({ commit, state }: { commit: Function; state: ItemState }, { id, updates }: { id: string, updates: Partial<SessionWithOrdersAndType | FolderWithSessions>}) {
            let originalItem: FolderWithSessions | SessionWithOrdersAndType | undefined = undefined;
            let itemLocation = '';
            let parentFolder: FolderWithSessions | undefined = undefined;
        
            // Search in direct arrays first
            const searchArrays = [
                { name: 'allFolders', array: state.allFolders },
                { name: 'allSessions', array: state.allSessions },
                { name: 'archivedItems', array: state.archivedItems }
            ];
        
            for (const { name, array } of searchArrays) {
                const foundItem = array.find(item => item.id === id);
                if (foundItem) {
                    originalItem = foundItem;
                    itemLocation = name;
                    break;
                }
            }
        
            // If not found in direct arrays, search within folder sessions
            if (!originalItem) {
                // Search in active folders
                for (const folder of state.allFolders) {
                    if (folder.sessions) {
                        const sessionIndex = folder.sessions.findIndex(session => session.id === id);
                        if (sessionIndex !== -1) {
                            originalItem = folder.sessions[sessionIndex] as SessionWithOrdersAndType;
                            itemLocation = 'nestedInActiveFolder';
                            parentFolder = folder;
                            break;
                        }
                    }
                }
        
                // If still not found, search in archived folders
                if (!originalItem) {
                    for (const archivedItem of state.archivedItems) {
                        if (archivedItem.type === 'folder' && 'sessions' in archivedItem && archivedItem.sessions) {
                            const sessionIndex = archivedItem.sessions.findIndex(session => session.id === id);
                            if (sessionIndex !== -1) {
                                originalItem = archivedItem.sessions[sessionIndex] as SessionWithOrdersAndType;
                                itemLocation = 'nestedInArchivedFolder';
                                parentFolder = archivedItem;
                                break;
                            }
                        }
                    }
                }
            }
        
            if (!originalItem) {
                throw new Error('Item not found in any collection');
            }
        
            const operationId = `update_${originalItem.type ?? 'item'}_${id}_${Date.now()}`;
            const originalData = { ...originalItem };
        
            // Create rollback function based on item location
            const rollback = () => {
                if (itemLocation === 'nestedInActiveFolder' && parentFolder) {
                    commit('updateSessionInFolder', { 
                        folderId: parentFolder.id, 
                        sessionId: id, 
                        updates: originalData,
                        isArchived: false
                    });
                } else if (itemLocation === 'nestedInArchivedFolder' && parentFolder) {
                    commit('updateSessionInFolder', { 
                        folderId: parentFolder.id, 
                        sessionId: id, 
                        updates: originalData,
                        isArchived: true
                    });
                } else {
                    // Item is in a direct array
                    commit('updateItemInArray', { 
                        arrayName: itemLocation, 
                        id, 
                        updates: originalData 
                    });
                }
        
                // Update selectedItems if present
                const selectedItem = state.selectedItems.find(i => i.id === id);
                if (selectedItem) {
                    Object.assign(selectedItem, originalData);
                }
            };
        
            // Apply optimistic update based on location
            if (itemLocation === 'nestedInActiveFolder' && parentFolder) {
                commit('updateSessionInFolder', { 
                    folderId: parentFolder.id, 
                    sessionId: id, 
                    updates,
                    isArchived: false
                });
            } else if (itemLocation === 'nestedInArchivedFolder' && parentFolder) {
                commit('updateSessionInFolder', { 
                    folderId: parentFolder.id, 
                    sessionId: id, 
                    updates,
                    isArchived: true
                });
            } else {
                // Item is in a direct array
                commit('updateItemInArray', { arrayName: itemLocation, id, updates });
            }
        
        
            commit('addPendingOperation', {
                id: operationId,
                type: 'update',
                itemId: id,
                itemLocation,
                parentFolderId: parentFolder?.id,
                timestamp: Date.now(),
                rollbackAction: rollback,
            });
        
            try {
                let result;
                if (originalItem.type === 'session') {
                    result = await updateSession(id, updates);
                } else if (originalItem.type === 'folder') {
                    result = await updateFolder(id, updates);
                } else {
                    throw new Error('Unknown item type for updating');
                }
                
                commit('removePendingOperation', operationId);
                
                // Update with server response if it differs
                if (result) {
                    if (itemLocation === 'nestedInActiveFolder' && parentFolder) {
                        commit('updateSessionInFolder', { 
                            folderId: parentFolder.id, 
                            sessionId: id, 
                            updates: result,
                            isArchived: false
                        });
                    } else if (itemLocation === 'nestedInArchivedFolder' && parentFolder) {
                        commit('updateSessionInFolder', { 
                            folderId: parentFolder.id, 
                            sessionId: id, 
                            updates: result,
                            isArchived: true
                        });
                    } else {
                        commit('updateItemInArray', { arrayName: itemLocation, id, updates: result });
                    }                    
                }
                
                return result;
            } catch (error: any) {
                // Rollback on failure
                rollback();
                commit('removePendingOperation', operationId);
                commit('setError', `Failed to update item: ${error.message}`);
                throw error;
            }
        },

        async archiveItem({ commit, state }: { commit: Function; state: ItemState }, id: string) {
            let originalItem: FolderWithSessions | SessionWithOrdersAndType | undefined = undefined;
            let sourceArray = '';
            let originalIndex = -1;
            let parentFolder: FolderWithSessions | undefined = undefined;
            let sessionIndexInFolder = -1;
            
            // First, search in direct arrays (allFolders and allSessions)
            const searchArrays = [
                { name: 'allFolders', array: state.allFolders },
                { name: 'allSessions', array: state.allSessions }
            ];
            
            for (const { name, array } of searchArrays) {
                const index = array.findIndex((item) => item.id === id);
                if (index !== -1) {
                    originalItem = array[index];
                    sourceArray = name;
                    originalIndex = index;
                    break;
                }
            }
            
            // If not found in direct arrays, search within sessions of folders
            if (!originalItem) {
                for (let i = 0; i < state.allFolders.length; i++) {
                    const folder = state.allFolders[i];
                    if (folder.sessions) {
                        const sessionIndex = folder.sessions.findIndex((session) => session.id === id);
                        if (sessionIndex !== -1) {
                            originalItem = folder.sessions[sessionIndex] as SessionWithOrdersAndType;
                            parentFolder = folder;
                            sessionIndexInFolder = sessionIndex;
                            sourceArray = 'nestedInFolder';
                            originalIndex = i; // Index of parent folder in allFolders
                            break;
                        }
                    }
                }
            }
            
            if (!originalItem) {
                throw new Error('Item not found in any collection');
            }
        
            const operationId = `archive_${originalItem.type ?? 'item'}_${id}_${Date.now()}`;
        
            // Create rollback function based on where the item was found
            const rollback = () => {
                if (sourceArray === 'nestedInFolder' && parentFolder) {
                    // Item was nested in a folder - restore it to the folder's sessions
                    commit('addSessionToFolder', { 
                        folderId: parentFolder.id, 
                        session: originalItem,
                        index: sessionIndexInFolder
                    });
                    // Remove from archived items
                    commit('removeItemFromArray', { arrayName: 'archivedItems', id });
                } else {
                    // Item was directly in allFolders or allSessions - restore it there
                    commit('restoreItemToPosition', { 
                        item: originalItem, 
                        array: sourceArray, 
                        index: originalIndex 
                    });
                    // Remove from archived items
                    commit('removeItemFromArray', { arrayName: 'archivedItems', id });
                }
            };
        
            // Apply optimistic updates based on source
            if (sourceArray === 'nestedInFolder' && parentFolder) {
                // Remove from nested folder sessions
                commit('removeSessionFromFolder', { 
                    folderId: parentFolder.id, 
                    sessionId: id 
                });
            } else {
                // Remove from direct array (allFolders or allSessions)
                commit('removeItemFromArray', { arrayName: sourceArray, id });
            }
            
            // Add to archived items in both cases
            commit('addItemToArray', { arrayName: 'archivedItems', item: originalItem });
            
            commit('addPendingOperation', {
                id: operationId,
                type: 'archive',
                itemId: id,
                sourceArray: sourceArray,
                parentFolderId: parentFolder?.id,
                timestamp: Date.now(),
                rollbackAction: rollback,
            });
        
            try {
                if (originalItem.type === 'folder') {
                    await archiveFolder(id);
                } else if (originalItem.type === 'session') {
                    await archiveSession(id);
                } else if (!originalItem.type) { // if the original item has no 'type' property, it is a session within a folder
                    await archiveSession(id);
                } else {
                    throw new Error(`Unknown item type for archiving: ${originalItem.type}`);
                }
                commit('removePendingOperation', operationId);
            } catch (error: any) {
                rollback();
                commit('removePendingOperation', operationId);
                commit('setError', `Failed to archive item: ${error.message}`);
                throw error;
            }
        },

        async unarchiveItem({ commit, state }: { commit: Function; state: ItemState }, id: string) {   
            let originalItem: FolderWithSessions | SessionWithOrdersAndType | undefined = undefined;
            let sourceArray = '';
            let originalIndex = -1;
            let parentFolder: FolderWithSessions | undefined = undefined;
            let sessionIndexInFolder = -1;
            
            // First, search in archivedItems array directly
            const archivedIndex = state.archivedItems.findIndex((item) => item.id === id);
            if (archivedIndex !== -1) {
                originalItem = state.archivedItems[archivedIndex];
                sourceArray = 'archivedItems';
                originalIndex = archivedIndex;
            } else {
                // If not found directly, search within sessions of archived folders
                for (let i = 0; i < state.archivedItems.length; i++) {
                    const archivedItem = state.archivedItems[i];
                    if (archivedItem.type === 'folder' && 'sessions' in archivedItem && archivedItem.sessions) {
                        const sessionIndex = archivedItem.sessions.findIndex((session) => session.id === id);
                        if (sessionIndex !== -1) {
                            originalItem = archivedItem.sessions[sessionIndex] as SessionWithOrdersAndType;
                            parentFolder = archivedItem;
                            sessionIndexInFolder = sessionIndex;
                            sourceArray = 'nestedInFolder';
                            originalIndex = i; // Index of parent folder in archivedItems
                            break;
                        }
                    }
                }
            }
            
            if (!originalItem) {
                throw new Error('Item not found in archived items or nested sessions');
            }
        
            const operationId = `unarchive_${originalItem.type ?? 'item'}_${id}_${Date.now()}`;
        
            // Create rollback function based on where the item was found
            const rollback = () => {
                if (sourceArray === 'archivedItems') {
                    // Item was directly in archivedItems - restore it there
                    commit('addItemToArray', { 
                        arrayName: 'archivedItems', 
                        item: originalItem,
                        index: originalIndex // Optional: restore to exact position
                    });
                    // Remove from its new location
                    if (originalItem!.type === 'folder') {
                        commit('removeItemFromArray', { arrayName: 'allFolders', id });
                    } else if (originalItem!.type === 'session') {
                        commit('removeItemFromArray', { arrayName: 'allSessions', id });
                    }
                } else if (sourceArray === 'nestedInFolder' && parentFolder) {
                    // Item was nested in a folder - restore it to the folder's sessions
                    commit('addSessionToArchivedFolder', { 
                        folderId: parentFolder.id, 
                        session: originalItem,
                        index: sessionIndexInFolder
                    });
                    // Remove from allSessions
                    commit('removeItemFromArray', { arrayName: 'allSessions', id });
                }
            };
        
            // Apply optimistic updates based on source
            if (sourceArray === 'archivedItems') {
                // Remove from archivedItems and add to appropriate active array
                commit('removeItemFromArray', { arrayName: 'archivedItems', id });
                if (originalItem.type === 'folder') {
                    commit('addItemToArray', { arrayName: 'allFolders', item: originalItem });
                } else if (originalItem.type === 'session') {
                    commit('addItemToArray', { arrayName: 'allSessions', item: originalItem });
                }
            } else if (sourceArray === 'nestedInFolder' && parentFolder) {
                // Remove from nested folder sessions and add to allSessions
                commit('removeSessionFromArchivedFolder', {
                    folderId: parentFolder.id, 
                    sessionId: id 
                });
                commit('addItemToArray', { arrayName: 'allSessions', item: originalItem });
            }
            
            commit('addPendingOperation', {
                id: operationId,
                type: 'unarchive',
                itemId: id,
                sourceArray: sourceArray,
                parentFolderId: parentFolder?.id,
                timestamp: Date.now(),
                rollbackAction: rollback,
            });
        
            try {
                if (originalItem.type === 'folder') {
                    await unarchiveFolder(id);
                } else if (originalItem.type === 'session' && sourceArray !== 'nestedInFolder') {
                    await unarchiveSession(id);
                } else if (originalItem.type === 'session' && sourceArray === 'nestedInFolder') { // if the original item has no 'type' property, it is a session within a folder
                    await unarchiveSession(id);
                    await updateSession(id, { folderId: null }); // Ensure session is marked as active
                } else {
                    throw new Error(`Unknown item type for unarchiving: ${originalItem.type}`);
                }
                commit('removePendingOperation', operationId);
            } catch (error: any) {
                rollback();
                commit('removePendingOperation', operationId);
                commit('setError', `Failed to unarchive item: ${error.message}`);
                throw error;
            }
        },

        async moveItem({ commit, state }: { commit: Function; state: ItemState }, { itemId, folderId }: { itemId: string, folderId: string | null }) {
            let sessionItem: SessionWithOrdersAndType | undefined = undefined;
            let currentLocation = '';
            let currentFolderId: string | undefined = undefined;
            let sessionIndexInFolder = -1;
            let sessionIndexInAllSessions = -1;
        
            // Find the session in allSessions
            sessionIndexInAllSessions = state.allSessions.findIndex(session => session.id === itemId);
            if (sessionIndexInAllSessions !== -1) {
                sessionItem = state.allSessions[sessionIndexInAllSessions];
                currentLocation = 'allSessions';
            }
        
            // If not found in allSessions, search within folder sessions
            if (!sessionItem) {
                for (const folder of state.allFolders) {
                    if (folder.sessions) {
                        const nestedSessionIndex = folder.sessions.findIndex(session => session.id === itemId);
                        if (nestedSessionIndex !== -1) {
                            sessionItem = folder.sessions[nestedSessionIndex] as SessionWithOrdersAndType;
                            currentLocation = 'folderSessions';
                            currentFolderId = folder.id;
                            sessionIndexInFolder = nestedSessionIndex;
                            break;
                        }
                    }
                }
            }
        
            if (!sessionItem) {
                throw new Error(`Session with id ${itemId} not found in any location`);
            }
        
            const operationId = `move_session_${itemId}_${Date.now()}`;
            const originalFolderId = (sessionItem as any).folderId;
        
            // Create rollback function
            const rollback = () => {
                // Find the session in its current location after the optimistic update
                let currentSessionItem = state.allSessions.find(session => session.id === itemId);
        
                if (!currentSessionItem) {
                    // Look in folder sessions
                    for (const folder of state.allFolders) {
                        if (folder.sessions) {
                            currentSessionItem = folder.sessions.find(session => session.id === itemId) as SessionWithOrdersAndType;
                            if (currentSessionItem) {
                                // Remove from current folder
                                const currentSessionIndex = folder.sessions.findIndex(session => session.id === itemId);
                                folder.sessions.splice(currentSessionIndex, 1);
                                break;
                            }
                        }
                    }
                } else {
                    // Remove from allSessions
                    const currentSessionIndex = state.allSessions.findIndex(session => session.id === itemId);
                    state.allSessions.splice(currentSessionIndex, 1);
                }
        
                if (currentSessionItem) {
                    // Restore original folderId
                    if ('folderId' in currentSessionItem) {
                        (currentSessionItem as any).folderId = originalFolderId;
                    }
        
                    // Restore to original location
                    if (currentLocation === 'allSessions') {
                        state.allSessions.splice(sessionIndexInAllSessions, 0, currentSessionItem);
                    } else if (currentLocation === 'folderSessions' && currentFolderId) {
                        const sourceFolder = state.allFolders.find(folder => folder.id === currentFolderId);
                        if (sourceFolder) {
                            if (!sourceFolder.sessions) {
                                sourceFolder.sessions = [];
                            }
                            sourceFolder.sessions.splice(sessionIndexInFolder, 0, currentSessionItem);
                        }
                    }
        
                    // Update selectedItems if present
                    const selectedItem = state.selectedItems.find(i => i.id === itemId);
                    if (selectedItem && 'folderId' in selectedItem) {
                        (selectedItem as any).folderId = originalFolderId;
                    }
                }
            };
        
            // Apply optimistic updates
            // Remove session from current location
            if (currentLocation === 'allSessions') {
                commit('removeItemFromArray', { arrayName: 'allSessions', id: itemId });
            } else if (currentLocation === 'folderSessions' && currentFolderId) {
                commit('removeSessionFromFolder', { folderId: currentFolderId, sessionId: itemId });
            }
        
            // Update session's folderId property
            const sessionToUpdate = { ...sessionItem };
            if ('folderId' in sessionToUpdate) {
                (sessionToUpdate as any).folderId = folderId;
            }
        
            // Add session to new location
            if (folderId === null) {
                // Moving to root level (allSessions)
                commit('addItemToArray', { arrayName: 'allSessions', item: sessionToUpdate });
            } else {
                // Moving to a specific folder
                commit('addSessionToFolder', { folderId, session: sessionToUpdate });
            }
        
            // Update selectedItems if present
            commit('updateSelectedItemFolderId', { itemId, folderId });
        
            commit('addPendingOperation', {
                id: operationId,
                type: 'moveToFolder',
                itemId: itemId,
                newFolderId: folderId,
                originalLocation: currentLocation,
                originalFolderId: currentFolderId,
                timestamp: Date.now(),
                rollbackAction: rollback,
            });
        
            try {
                // Make API call
                await updateSession(itemId, { folderId });
                commit('removePendingOperation', operationId);
            } catch (error: any) {
                // Rollback on failure
                rollback();
                commit('removePendingOperation', operationId);
                commit('setError', `Failed to move session: ${error.message}`);
                throw error;
            }
        },

        async addSessionToAllSessions({ commit }: { commit: Function; state: ItemState}, { item }: { item: SessionWithOrdersAndType }){

            item = {
                ...item,
                type: "session",
                uploads: [],
                noteStatus: '',
                orderStatus: '',
            }
            commit('addItemToArray', { arrayName: 'allSessions', item: item });
        },

        async addUploadToSession({ commit }: { commit: Function }, { sessionId, upload }: { sessionId: string; upload: Upload }){

            if (!sessionId || !upload) {
                throw new Error('Session ID and upload are required');
            }
            commit('addUploadToSession', { sessionId, upload });
            commit('addUploadToFolderSession', { sessionId, upload });
        },
        
        async loadAndProcessFolder({ commit }: { commit: Function; }, {folderId, array }: {folderId: string, array: string}) {

            const folder: FolderWithSessions = await getFolder(folderId);

            // Process the sessions
            commit('processFolderSessions', {folder, array});
        }
    },
    getters: {
        getAllItems(state: { allItems: (SessionWithOrdersAndType | FolderWithSessions)[] }): (SessionWithOrdersAndType | FolderWithSessions | Folder)[] {
            return state.allItems;
        },
        getAllFolders(state: { allFolders: FolderWithSessions[] }): FolderWithSessions[] {
            return state.allFolders;
        },
        getAllSessions(state: { allSessions: SessionWithOrdersAndType[] }): SessionWithOrdersAndType[] {
            return state.allSessions;
        },
        getArchivedItems(state: { archivedItems: (SessionWithOrdersAndType | FolderWithSessions)[] }): (SessionWithOrdersAndType | FolderWithSessions)[] {
            return state.archivedItems;
        },
        getSelectedItems(state: { selectedItems: (SessionWithOrdersAndType | FolderWithSessions)[] }): (SessionWithOrdersAndType | FolderWithSessions)[] {
            return state.selectedItems;
        },
        getFolderById: (state: ItemState) => (folderId: string) => {
            return state.allFolders.find(folder => folder.id === folderId);
        },
        getFolderIndexById: (state: ItemState) => (folderId: string, array: string) => {
            if (array === 'allFolders') {
                return state.allFolders.findIndex(folder => folder.id === folderId);
            } else if (array === 'archivedItems') {
                return state.archivedItems.findIndex(item => item.id === folderId && item.type === 'folder');
            }
        },
        getSessionById: (state: ItemState) => (sessionId: string) => {
            return state.allSessions.find(item => item.id === sessionId) ||
                state.archivedItems.find(item => item.id === sessionId);
        },
        foldersListLoading: (state: ItemState) => state.foldersListLoading,
        allItemsListLoading: (state: ItemState) => state.allItemsListLoading,
        archivedListLoading: (state: ItemState) => state.archivedListLoading,
    },
};